/**
 * Tag Quality Indicator Component
 *
 * Provides real-time feedback on tag quality for AlgoRhythm recommendation engine.
 * Helps users and developers understand tag effectiveness during asset registration.
 */

import React from 'react';
import {
  Box,
  Chip,
  Tooltip,
  Typography,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import TagQualityValidator, {
  TagAnalysis,
} from '../../utils/tagQualityValidator';

interface TagQualityIndicatorProps {
  tags: string[];
  metadata?: {
    hairColor?: string;
    hairStyle?: string;
    makeupType?: string;
    musicalStyle?: string[];
    ageGroup?: string;
    archetype?: string;
    gender?: string;
    eyeColor?: string;
    skinTone?: string;
  };
  showDetails?: boolean;
  compact?: boolean;
  onAddTag?: (tag: string) => void; // New prop for adding tags
  onRemoveTag?: (tag: string) => void; // New prop for removing tags
}

const TagQualityIndicator: React.FC<TagQualityIndicatorProps> = ({
  tags,
  metadata,
  showDetails = false,
  compact = false,
  onAddTag,
  onRemoveTag,
}) => {
  if (!tags || tags.length === 0) {
    return null;
  }

  const analysis: TagAnalysis = TagQualityValidator.analyzeTagSet(
    tags,
    metadata
  );
  const idealTags = metadata
    ? TagQualityValidator.generateIdealTags(metadata)
    : [];
  const comparison =
    idealTags.length > 0
      ? TagQualityValidator.compareToIdeal(tags, idealTags)
      : null;

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getScoreIcon = (score: number) => {
    if (score >= 80) return <CheckCircleIcon color="success" />;
    if (score >= 60) return <WarningIcon color="warning" />;
    return <ErrorIcon color="error" />;
  };

  const getQualityChipColor = (category: string) => {
    switch (category) {
      case 'excellent':
        return 'success';
      case 'good':
        return 'info';
      case 'fair':
        return 'warning';
      case 'poor':
        return 'error';
      default:
        return 'default';
    }
  };

  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getScoreIcon(analysis.averageScore)}
        <Typography variant="body2" color="text.secondary">
          Tag Quality: {analysis.averageScore}/100
        </Typography>
        {comparison && (
          <Typography variant="body2" color="text.secondary">
            • Match: {comparison.matchScore}%
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {/* Match Score */}
      {comparison && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Match with Ideal Tags: {comparison.matchScore}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={comparison.matchScore}
            color={getScoreColor(comparison.matchScore)}
            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
          />
        </Box>
      )}

      {/* Recommendations */}
      {analysis.recommendations.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            Recommendations:
          </Typography>
          {analysis.recommendations.map((rec, index) => (
            <Typography key={index} variant="body2">
              • {rec}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Missing High-Value Tags */}
      {comparison && comparison.missing.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            Missing High-Value Tags:
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
            {comparison.missing.slice(0, 8).map((tag, index) => (
              <Chip
                key={index}
                label={onAddTag ? `+ ${tag}` : tag}
                size="small"
                variant="outlined"
                color="warning"
                clickable={!!onAddTag}
                onClick={onAddTag ? () => onAddTag(tag) : undefined}
                sx={
                  onAddTag
                    ? {
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'warning.light' },
                      }
                    : undefined
                }
              />
            ))}
          </Box>
          {onAddTag && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: 'block' }}
            >
              💡 Click any tag above to add it instantly
            </Typography>
          )}
        </Alert>
      )}

      {/* Detailed Analysis */}
      {showDetails && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">Detailed Tag Analysis</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {tags.map((tag, index) => {
                const tagScore = TagQualityValidator.scoreTag(tag);
                return (
                  <Tooltip
                    key={index}
                    title={
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {tag}: {tagScore.score}/100
                        </Typography>
                        {tagScore.feedback.map((feedback, i) => (
                          <Typography key={i} variant="body2">
                            {feedback}
                          </Typography>
                        ))}
                      </Box>
                    }
                  >
                    <Chip
                      label={`${tag} (${tagScore.score})`}
                      size="small"
                      color={getQualityChipColor(tagScore.category)}
                      variant="outlined"
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default TagQualityIndicator;
